import { KycPreReviewIdentity } from '../../kyc-pre-review-modal/kyc-pre-review-modal.component';

/**
 * SomosKycV1 no-QR OCR pre-check scoring.
 *
 * Pure, framework-free, unit-testable. Compares the identity extracted by the standalone Textract
 * lambda against the user's stored PII and produces a single weighted confidence score plus a
 * decision:
 *   - 'proceed' → the document clearly belongs to the user; skip the pre-review modal and submit.
 *   - 'review'  → ambiguous/low/mismatch; open the pre-review modal (confirm / amend / new doc /
 *     proceed-anyway) exactly as before.
 *
 * All weights and thresholds are configurable (see SomosOcrScoreWeights /
 * SOMOS_OCR_SCORE_WEIGHTS_DEFAULTS); the values here are only the algorithm — defaults live in
 * somos-ocr-precheck.config.ts and can be overridden per action via cp_actions.config.somosOcrPreCheck.
 */

/** A single weighted contribution, kept for logging/debug. */
export interface ScoreBreakdownItem {
  criterion: string;
  points: number;
  detail: string;
}

export interface PreCheckScoreResult {
  score: number;
  breakdown: ScoreBreakdownItem[];
  decision: 'proceed' | 'review';
}

/** Configurable weights + thresholds for the pre-check score. All values are tunable per action. */
export interface SomosOcrScoreWeights {
  // ----- Name -----
  /** Penalty when the document has no readable name at all (and the user has one). */
  nameNotDetected: number;
  /** Max points per user name-token that finds a match; scaled down by edit distance. */
  namePerTokenMatch: number;
  /** Penalty when a name WAS detected but no user token matched any candidate token. */
  nameAllDifferent: number;
  /** Ignore name tokens shorter than this (drops single initials); connectors are always dropped. */
  nameMinTokenLength: number;
  /**
   * Minimum per-token similarity (0..1) for a user token to count as matched. Below this the token
   * earns 0 and does not count toward "name matched". Stops unrelated tokens from accumulating small
   * fuzzy partial credit into a false positive, while still crediting genuine OCR near-misses.
   */
  nameTokenMinSimilarity: number;

  // ----- CPF -----
  /** Max points when the (valid) document CPF matches the user's; scaled down by digit distance. */
  cpfMatch: number;
  /** Digit edit-distance at/below which the CPFs are "similar"; above this → very different. */
  cpfSimilarityMaxDistance: number;
  /** Penalty when a VALID document CPF is clearly a different person's. */
  cpfVeryDifferent: number;
  /** Small penalty when the document has no CPF (and the user has one). */
  cpfNotDetected: number;

  // ----- DOB -----
  /** Points when the document DOB matches the user's (same ISO date or within day tolerance). */
  dobMatch: number;
  /** Allow this many days of difference and still count the DOB as a match (OCR/format slips). */
  dobDayTolerance: number;
  /** Penalty when the document DOB is clearly different. */
  dobDifferent: number;
  /** Contribution when the document has no DOB (usually 0 — DOB is often absent on IDs). */
  dobNotDetected: number;

  // ----- Combined signal -----
  /** Bonus when BOTH the name and the CPF independently matched (strong joint evidence). */
  nameAndCpfBothMatchBonus: number;

  // ----- Decision -----
  /** score >= proceedThreshold → 'proceed' (skip modal); otherwise 'review' (show modal). */
  proceedThreshold: number;
}

// Brazilian name connectors that carry no identifying signal.
const NAME_CONNECTORS = new Set(['de', 'da', 'do', 'dos', 'das', 'e']);

/** Plain Levenshtein edit distance (pure, no deps). */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/** Accent/case-insensitive fold. */
function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Significant name tokens: folded, connectors + short tokens dropped. */
function nameTokens(name: string | null | undefined, minLen: number): string[] {
  if (!name) return [];
  return fold(name)
    .split(/\s+/)
    .filter((t) => t.length >= minLen && !NAME_CONNECTORS.has(t));
}

/**
 * Brazilian CPF check-digit (mod 11) validation. Rejects wrong length and all-equal sequences
 * (e.g. 00000000000, 11111111111) which pass the arithmetic but are never real.
 */
export function isValidCpf(digits: string | null | undefined): boolean {
  if (!digits) return false;
  const d = digits.replace(/\D/g, '');
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;
  const calc = (len: number): number => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += parseInt(d[i], 10) * (len + 1 - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return calc(9) === parseInt(d[9], 10) && calc(10) === parseInt(d[10], 10);
}

/**
 * Score one extracted name candidate against the user's name tokens.
 * Returns the summed points and whether any user token matched at all.
 * Each user token earns its best similarity across the candidate's tokens, where
 * similarity = max(0, 1 - distance / maxLen) so an exact token earns the full weight and a
 * near-miss earns partial credit.
 */
function scoreNameCandidate(
  candidate: string,
  userTokens: string[],
  weights: SomosOcrScoreWeights,
): { points: number; anyMatch: boolean } {
  const candTokens = nameTokens(candidate, weights.nameMinTokenLength);
  if (!candTokens.length || !userTokens.length) return { points: 0, anyMatch: false };
  let points = 0;
  let anyMatch = false;
  for (const ut of userTokens) {
    let bestSim = 0;
    for (const ct of candTokens) {
      const maxLen = Math.max(ut.length, ct.length);
      const sim = maxLen === 0 ? 0 : Math.max(0, 1 - levenshtein(ut, ct) / maxLen);
      if (sim > bestSim) bestSim = sim;
    }
    // Only a genuine match (>= floor) counts — unrelated tokens earn 0 and don't accumulate noise.
    if (bestSim < weights.nameTokenMinSimilarity) continue;
    anyMatch = true;
    points += weights.namePerTokenMatch * bestSim;
  }
  return { points, anyMatch };
}

/**
 * Compute the weighted pre-check score + decision.
 *
 * A criterion only contributes when the user actually has the corresponding field on file — we do
 * not penalize users with incomplete PII for what the document does or doesn't show.
 *
 * Default-threshold reasoning (see SOMOS_OCR_SCORE_WEIGHTS_DEFAULTS): with namePerTokenMatch=5 and
 * proceedThreshold=9, a solid two-token name match (first+last ≈ +10) clears it, and name+CPF
 * (≈ +5 +5 (+2 bonus)) clears it, but a lone CPF match (+5), a single matching token (+5), or a
 * matching CPF beside a different name (+5 -1) all stay in 'review'. Clear mismatches go sharply
 * negative (different valid CPF = -10).
 */
export function scorePreCheck(
  extracted: KycPreReviewIdentity,
  user: KycPreReviewIdentity,
  weights: SomosOcrScoreWeights,
): PreCheckScoreResult {
  const breakdown: ScoreBreakdownItem[] = [];
  const add = (criterion: string, points: number, detail: string) => {
    if (points !== 0) breakdown.push({ criterion, points, detail });
  };

  // ---------- NAME ----------
  let nameMatched = false;
  if (user.fullName) {
    const userTokens = nameTokens(user.fullName, weights.nameMinTokenLength);
    const candidates = [extracted.fullName, ...(extracted.nameAlternates ?? [])].filter(
      (c): c is string => !!c && !!c.trim(),
    );
    if (!candidates.length) {
      add('nameNotDetected', weights.nameNotDetected, 'document had no readable name');
    } else if (!userTokens.length) {
      // user name has no significant tokens to compare — neutral.
    } else {
      let best = { points: 0, anyMatch: false };
      for (const cand of candidates) {
        const s = scoreNameCandidate(cand, userTokens, weights);
        if (s.points > best.points) best = s;
        else if (s.anyMatch && !best.anyMatch) best.anyMatch = true;
      }
      if (best.anyMatch) {
        nameMatched = true;
        const pts = Math.round(best.points * 100) / 100;
        add('nameTokenMatch', pts, `best name candidate matched user tokens (≈${pts})`);
      } else {
        add('nameAllDifferent', weights.nameAllDifferent, 'name detected but no token matched');
      }
    }
  }

  // ---------- CPF ----------
  let cpfMatched = false;
  if (user.cpfDigits) {
    const docCpf = (extracted.cpfDigits ?? '').replace(/\D/g, '');
    if (!docCpf) {
      add('cpfNotDetected', weights.cpfNotDetected, 'document had no CPF');
    } else if (!isValidCpf(docCpf)) {
      // Invalid CPF (failed check digits) → OCR noise, do not score.
      add('cpfInvalid', 0, 'document CPF failed check-digit validation (ignored)');
    } else {
      const userCpf = user.cpfDigits.replace(/\D/g, '');
      const dist = levenshtein(docCpf, userCpf);
      if (dist <= weights.cpfSimilarityMaxDistance) {
        cpfMatched = true;
        // Full points at distance 0, scaled toward 0 as distance approaches the threshold.
        const sim = Math.max(0, 1 - dist / (weights.cpfSimilarityMaxDistance + 1));
        const pts = Math.round(weights.cpfMatch * sim * 100) / 100;
        add('cpfMatch', pts, `CPF matched (distance ${dist})`);
      } else {
        add('cpfVeryDifferent', weights.cpfVeryDifferent, `valid CPF, different person (distance ${dist})`);
      }
    }
  }

  // ---------- DOB ----------
  if (user.dobIso) {
    if (!extracted.dobIso) {
      add('dobNotDetected', weights.dobNotDetected, 'document had no DOB');
    } else if (dobWithinTolerance(extracted.dobIso, user.dobIso, weights.dobDayTolerance)) {
      add('dobMatch', weights.dobMatch, 'DOB matched');
    } else {
      add('dobDifferent', weights.dobDifferent, 'DOB different');
    }
  }

  // ---------- COMBINED ----------
  if (nameMatched && cpfMatched) {
    add('nameAndCpfBothMatch', weights.nameAndCpfBothMatchBonus, 'name AND CPF both matched');
  }

  const score = Math.round(breakdown.reduce((acc, b) => acc + b.points, 0) * 100) / 100;
  const decision: 'proceed' | 'review' = score >= weights.proceedThreshold ? 'proceed' : 'review';
  return { score, breakdown, decision };
}

/** True when two ISO dates are equal or within `dayTolerance` days. */
function dobWithinTolerance(a: string, b: string, dayTolerance: number): boolean {
  if (a === b) return true;
  if (dayTolerance <= 0) return false;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (isNaN(ta) || isNaN(tb)) return false;
  const days = Math.abs(ta - tb) / 86_400_000;
  return days <= dayTolerance;
}
